import type { Report, ReportDraft, ReportRunResult } from './types';

const CSRF_COOKIE = 'pacebi_csrf';

function csrfToken(): string {
  // Cookie set by /api/login + every /api/me refresh; double-submit pattern.
  const m = document.cookie.match(new RegExp('(?:^|; )' + CSRF_COOKIE + '=([^;]+)'));
  return m ? decodeURIComponent(m[1]) : '';
}

export async function fetchReports(): Promise<Report[]> {
  const res = await fetch('/api/reports', { credentials: 'same-origin' });
  if (res.status === 403) throw new Error('forbidden');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as Report[];
}

export async function fetchReport(id: string): Promise<Report> {
  const res = await fetch('/api/reports/' + encodeURIComponent(id), {
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as Report;
}

export async function createReport(draft: ReportDraft): Promise<Report> {
  const res = await fetch('/api/reports', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify(draft),
  });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()) as Report;
}

export async function updateReport(id: string, draft: ReportDraft): Promise<Report> {
  const res = await fetch('/api/reports/' + encodeURIComponent(id), {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify(draft),
  });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()) as Report;
}

export async function deleteReport(id: string): Promise<void> {
  const res = await fetch('/api/reports/' + encodeURIComponent(id), {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { 'X-CSRF-Token': csrfToken() },
  });
  if (!res.ok && res.status !== 204) throw new Error(await res.text());
}

export async function duplicateReport(id: string): Promise<Report> {
  const res = await fetch('/api/reports/' + encodeURIComponent(id) + '/duplicate', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-CSRF-Token': csrfToken() },
  });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()) as Report;
}

/**
 * Run-time overrides (JCD-17 phase 3 — dashboard report widgets): override
 * the saved report's own from/to/sid/anum for this run only, without editing
 * the saved report. Omit for the report's own filters unchanged.
 */
export interface ReportRunOverrides {
  from?: string;
  to?: string;
  sid?: number;
  anum?: number;
}

function overridesQS(overrides?: ReportRunOverrides): string {
  if (!overrides) return '';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(overrides)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? '?' + parts.join('&') : '';
}

export async function runReport(id: string, overrides?: ReportRunOverrides): Promise<ReportRunResult> {
  const res = await fetch('/api/reports/' + encodeURIComponent(id) + '/run' + overridesQS(overrides), {
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()) as ReportRunResult;
}

export async function previewReport(draft: ReportDraft): Promise<ReportRunResult> {
  const res = await fetch('/api/reports/run', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify(draft),
  });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()) as ReportRunResult;
}

/** Trigger a CSV download via <a>. Browser streams the body — no in-memory buffering. */
export function downloadReportCsv(id: string, name: string): void {
  const a = document.createElement('a');
  a.href = '/api/reports/' + encodeURIComponent(id) + '/run?format=csv';
  a.download = name.replace(/[^A-Za-z0-9_-]/g, '_') + '.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
